import { useEffect, useState, useRef, useCallback } from "react";
import { saveToState, loadFromState, resetState, getPaginatedData } from "../utils";
import { getNodeSetNodes, getNetworkNodeSets, getTopRelated } from "../api/quontxApi";
import AsyncSelect from "react-select/async";
import Select from "react-select";

const TopRelatedFilter = ({ propagnateData, isLoading = false }) => {
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState(null);
  const [hasNetworkError, setHasNetworkError] = useState(false);
  const [nodeOptions, setNodeOptions] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const [activeNode, setActiveNode] = useState(null);
  const [activeNodeSet, setActiveNodeSet] = useState(null);
  const [activeSchema, setActiveSchema] = useState(null);

  const resetFilters = () => {
    resetState("topRelatedState", [
      { stateFunc: setActiveNode, nullVal: null },
      { stateFunc: setActiveNodeSet, nullVal: null },
      { stateFunc: setActiveSchema, nullVal: null },
    ]);
    setHasNetworkError(false);
    setData(null);
    setNodeOptions([]);
  };

  useEffect(() => {
    loadFromState("topRelatedState", [
      { stateFunc: setActiveNode, storageObjectKey: "activeNode", defaultVal: null },
      { stateFunc: setActiveNodeSet, storageObjectKey: "activeNodeSet", defaultVal: null },
      { stateFunc: setActiveSchema, storageObjectKey: "activeSchema", defaultVal: null },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  // ---------------------------
  // NodeSet async search (only called when user types)
  // ---------------------------

  const loadNodeSetOptions = async (inputValue) => {
    const q = (inputValue || "").trim();
    if (q.length < 2) return [];

    const nodeSets = await getNetworkNodeSets({
      itemsPerPage: 100,
      name: q,
    });

    return (nodeSets?.data?.data ?? []).map((ns) => ({
      label: ns.description,
      value: ns.uuid,
      uuid: ns.uuid,
    }));
  };

  // ---------------------------
  // Load nodes for selected nodeset (only after nodeset is selected)
  // ---------------------------

  const loadNodeOptions = useCallback(async () => {
    const nodeSetId = activeNodeSet?.value;
    if (!nodeSetId) return;

    const nodes = await getPaginatedData(getNodeSetNodes, {
      itemsPerPage: 100,
      nodeSetId,
    });

    setNodeOptions(nodes?.data ?? []);
  }, [activeNodeSet]);

  useEffect(() => {
    // When node set cleared -> clear dependent selections/options
    if (!activeNodeSet) {
      setNodeOptions([]);
      setActiveNode(null);
      setData(null);
      return;
    }

    (async () => {
      try {
        await loadNodeOptions();
      } catch (e) {
        // optional
        // console.warn("Failed loading nodes for nodeset", e);
      }
    })();
  }, [activeNodeSet, loadNodeOptions]);

  // ---------------------------
  // Schema auto-default based on selection
  // ---------------------------

  useEffect(() => {
    if (activeNode) {
      setActiveSchema({ label: "Node to Node", value: "n2n" });
      return;
    }

    if (!activeNode && activeNodeSet) {
      setActiveSchema({ label: "Node Set to Node Set", value: "ns2ns" });
      return;
    }

    setActiveSchema(null);
  }, [activeNode, activeNodeSet]);

  // ---------------------------
  // Persist local storage
  // ---------------------------

  useEffect(() => {
    if (activeNode || activeNodeSet || activeSchema) {
      saveToState("topRelatedState", {
        activeNode,
        activeNodeSet,
        activeSchema,
      });
    }
  }, [activeNode, activeNodeSet, activeSchema]);

  // ---------------------------
  // Fetch top-related (only when valid input exists)
  // ---------------------------

  const loadTopRelated = useCallback(async () => {
    setHasNetworkError(false);

    const schema = activeSchema?.value;
    if (!schema) return;

    // Node-based query
    if (activeNode?.value) {
      if (schema === "ns2ns" || schema === "ns2n") return;

      const res = await getTopRelated({
        k: 100,
        node: activeNode.value,
        schema,
      });

      setData(res.data);
      return;
    }

    // NodeSet-based query
    if (activeNodeSet?.value) {
      if (schema === "n2n") return;

      const k = schema === "ns2ns" ? 10 : 100;

      const res = await getTopRelated({
        k,
        node: activeNodeSet.value,
        schema,
      });

      setData(res.data);
      return;
    }
  }, [activeSchema, activeNode, activeNodeSet]);

  useEffect(() => {
    // do nothing until user has at least selected something meaningful
    if (!activeSchema?.value) return;
    if (!activeNode?.value && !activeNodeSet?.value) return;

    (async () => {
      setLoadingData(true);
      try {
        await loadTopRelated();
      } catch (e) {
        setHasNetworkError(true);
        setData(null);
      } finally {
        setLoadingData(false);
      }
    })();
  }, [activeNode, activeNodeSet, activeSchema, loadTopRelated]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      <div className="flex flex-col gap-4 items-center justify-center mb-8 w-full">
        <div className="flex gap-4 justify-center mb-8 w-full">
          <div className="w-[500px] flex flex-col gap-2">
            <p>Node set</p>
            <AsyncSelect
              cacheOptions
              defaultOptions={[]}
              loadOptions={loadNodeSetOptions}
              onChange={(selected) => setActiveNodeSet(selected)}
              value={activeNodeSet}
              placeholder="Search node set..."
              isClearable
            />
          </div>
        </div>

        <div className="flex gap-4 justify-center mb-8 w-full">
          <div className="w-[500px] flex flex-col gap-2">
            <p>Node</p>
            <Select
              isSearchable={true}
              isDisabled={!activeNodeSet}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isDisabled ? "red" : "grey",
                }),
              }}
              value={activeNode}
              onChange={(selected) => setActiveNode(selected)}
              options={(nodeOptions ?? []).map((node) => ({
                label: node.name,
                value: node.uuid,
              }))}
              isClearable
              placeholder={!activeNodeSet ? "Select a node set first…" : "Select node…"}
            />
          </div>
        </div>

        <div className="flex gap-4 justify-center mb-8 w-full">
          <div className="w-[500px] flex flex-col gap-2">
            <p>Schema</p>
            <Select
              value={activeSchema}
              onChange={(selected) => setActiveSchema(selected)}
              options={[
                {
                  label: "Node to Node",
                  value: "n2n",
                  isDisabled: !activeNode && activeNodeSet,
                },
                {
                  label: "Node Set to Node Set",
                  value: "ns2ns",
                  isDisabled: !!activeNode,
                },
                {
                  label: "Node Set to Node",
                  value: "ns2n",
                  isDisabled: !!activeNode && !!activeNodeSet,
                },
              ]}
              isDisabled={!activeNode && !activeNodeSet}
              placeholder={!activeNode && !activeNodeSet ? "Select a node set or node first…" : "Select…"}
            />
          </div>
        </div>

        <div>
          <button
            onClick={resetFilters}
            className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
          >
            Reset filters
          </button>
        </div>

        {hasNetworkError && <p>Failed to load comparison data</p>}
        {(isLoading || loadingData) && <p>Loading data...</p>}

        {!loadingData && data && (
          <div>
            <table className="min-w-full">
              <thead className="bg-qtx-dark">
                <tr>
                  <th></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold">
                    Label
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold">
                    Node idx
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold">
                    Edge weight
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-qtx-gray">
                {data.flatMap((topRelated, idx) => (
                  <tr key={`${topRelated.label}-${idx}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                      {idx + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray">
                      {topRelated.label}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray">
                      {topRelated.node_idx}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray">
                      {topRelated.edge_weight}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopRelatedFilter;